import React, { lazy, useContext, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import V3Screens from 'screens/V3';
import AccountContext from '../../../../../contexts/AccountContext';
import { V3AppsContext } from '../../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import FeatureGate, { FEATURE_GATES } from 'components/FeatureGate';
import ALL_SETS_ROUTES from 'screens/DataManager/AllSets/routes';
import DatasetDefinitionScreens from 'screens/DataManager/DatasetDefinitions';
import SPECIFIC_SET_ROUTES from 'screens/DataManager/SpecificSet/routes';
import COSTS_SET_ROUTES from 'screens/DataManager/CostsSet/routes';
import appRoutes from '../../../../appRoutes';
import EntityDetailsShowRedirect from '../../../../../screens/EntityDetailsShowRedirect';
import EntityDetailsShow from '../../../../../screens/EntityDetailsShow';
import AgentManagement from '../../../../../screens/AgentManagement';
import InsightsChatScreen from '../../../../../screens/InsightsChat';
import { IS_INSIGHTS_ENABLED } from '../../../../../hooks/useInsightsChatBudget';
import NotFoundPage from '../../../../../screens/NotFoundPage';
import MappedEntityApp from './MappedEntityApp';
import PermissionGates from '../../../../../components/PermissionGates';
import PERMISSIONS from '../../../../../permissionDefinitions';
import DataManagerTargetManagerScreen from '../../../../../screens/DataManager/SpecificSet/DataManagerTargetManagerScreen';
import MyAccountScreen from '../../../../../screens/MyAccount';
import StudioRouteGate from '../../../../../screens/Studio/StudioRouteGate';

const AllSets = lazy(() => import('screens/DataManager/AllSets'));
const Metrics = lazy(() => import('screens/DataManager/Metrics'));
const Search = lazy(() => import('screens/DataManager/Search'));
const Costs = lazy(() => import('screens/DataManager/Costs'));
const EntityDefinitions = lazy(
  () => import('screens/DataManager/EntityDefinitions'),
);
const SpecificSet = lazy(() => import('screens/DataManager/SpecificSet'));
const ValidationReport = lazy(
  () => import('screens/DataManager/SpecificSet/ValidationReport'),
);
const DatasetFilters = lazy(() => import('screens/DataManager/DatasetFilters'));
const Explore = lazy(() => import('screens/DataManager/Explore'));
const Studio = lazy(() => import('screens/Studio'));
const StudioContentLoader = lazy(
  () => import('screens/Studio/StudioContentLoader'),
);

const CostsSet = lazy(() => import('screens/DataManager/CostsSet'));
const ActivitySettingsCreation = lazy(
  () => import('screens/DataManager/Costs/Settings/ActivitySettingsCreation'),
);
const CostsShow = lazy(() => import('screens/DataManager/Costs/CostsShow'));
const WallBoardBuilder = lazy(() => import('screens/WallBoardIndex'));
const ShowWallBoardBuilder = lazy(() => import('screens/WallBoardBuilderShow'));
const WallBoard = lazy(() => import('screens/WallBoard'));
const BoardsScreen = lazy(() => import('../../../../../screens/Boards/Boards'));
const BoardShowScreen = lazy(() => import('../../../../../screens/BoardShow'));
const SignOutScreen = lazy(() => import('../../../../../screens/SignOut'));

const HomeRoute = () => {
  const { homeProductId } = useContext(AccountContext);
  const { availableProducts } = useContext(V3AppsContext);

  const homeProduct = useMemo(() => {
    if (!homeProductId) return undefined;
    return availableProducts.find((p) => p.id === homeProductId);
  }, [availableProducts, homeProductId]);

  if (homeProduct) {
    return <Navigate to={`/apps/${homeProduct.urlSlug}`} replace />;
  }

  return <V3Screens.Home />;
};

const Content = () => {
  return (
    <Routes>
      <Route path="/" element={<HomeRoute />} />
      <Route path="/favourites" element={<V3Screens.FavouriteIndex />} />
      <Route path="/content" element={<V3Screens.ContentIndex />} />
      <Route path="/content/:contentId" element={<V3Screens.Content />} />
      {/* Support for old bookmarks */}
      <Route path="/reports/:contentId" element={<V3Screens.Content />} />
      <Route path="/dashboards/:contentId" element={<V3Screens.Content />} />
      <Route path="/kpi-lists/:contentId" element={<V3Screens.Content />} />
      <Route path="/goals/:contentId" element={<V3Screens.Content />} />

      <Route path="/apps/:productSlug/*" element={<V3Screens.App />} />
      <Route path={'/boards'} element={<BoardsScreen />} />
      <Route path={'/boards/:boardId'} element={<BoardShowScreen />} />
      <Route
        path={'/views/:appSlug/:mappedEntityNavItemKey'}
        element={<MappedEntityApp />}
      />
      <Route
        path={'/views/:appSlug/:mappedEntityNavItemKey/*'}
        element={<MappedEntityApp />}
      />
      <Route path="/workspaces" element={<V3Screens.WorkspaceIndex />} />
      <Route
        path="/workspaces/:workspaceSlug"
        element={<V3Screens.Workspace />}
      />
      <Route
        path="/workspaces/:workspaceSlug/:itemKey/*"
        element={<V3Screens.Workspace />}
      />
      {/* Entity App Redirects (non-mapped) */}
      <Route
        path={appRoutes.loggedIn.entityAppRedirect}
        element={<EntityDetailsShowRedirect />}
      />
      <Route
        path={appRoutes.loggedIn.showEntityApp}
        element={<EntityDetailsShow />}
      />
      <Route
        path={appRoutes.loggedIn.showEntityAppContentTab}
        element={<EntityDetailsShow />}
      />
      {/* Studio: Create New opens it on new content (see buildStudioNew) */}
      <Route
        path={appRoutes.loggedIn.studio}
        element={
          <StudioRouteGate>
            <Studio />
          </StudioRouteGate>
        }
      />
      <Route
        path={appRoutes.loggedIn.studioEdit}
        element={
          <StudioRouteGate>
            <StudioContentLoader />
          </StudioRouteGate>
        }
      />
      {/* Wallboards */}
      <Route
        path={appRoutes.loggedIn.wallBoardConfigs}
        element={<WallBoardBuilder />}
      />
      <Route
        path={appRoutes.loggedIn.showWallBoardConfig}
        element={<ShowWallBoardBuilder />}
      />
      <Route path={appRoutes.loggedIn.showWallBoard} element={<WallBoard />} />

      <Route path="/settings/*" element={<V3Screens.Settings />} />
      {/* Registered BEFORE the chat route: '/insights/agent/*' must not be
          captured by the chat's '/insights' path. Not registered at all while
          Insights is off, so its URLs are Not Found (see IS_INSIGHTS_ENABLED) */}
      {IS_INSIGHTS_ENABLED && (
        <Route path="/insights/agent/*" element={<AgentManagement />} />
      )}
      <Route
        path={appRoutes.loggedIn.insightsChat}
        element={
          <FeatureGate featureName={FEATURE_GATES.AI_INSIGHTS_CHAT}>
            <InsightsChatScreen />
          </FeatureGate>
        }
      />
      {/* Data Manager - Base */}
      <Route path={ALL_SETS_ROUTES.BASE} element={<AllSets />}>
        <Route
          path={ALL_SETS_ROUTES.DATASETS}
          element={<DatasetDefinitionScreens.DatasetsIndex />}
        />
        <Route
          path={ALL_SETS_ROUTES.METRICS}
          element={<Metrics mode="Metric" />}
        />

        <Route
          path={ALL_SETS_ROUTES.COMPOUND_METRICS}
          element={<Metrics mode="CompoundMetric" />}
        />
        <Route path={ALL_SETS_ROUTES.SEARCH} element={<Search />} />
        <Route path={ALL_SETS_ROUTES.COSTS} element={<Costs />} />
        <Route
          path={ALL_SETS_ROUTES.ENTITIES}
          element={
            <FeatureGate featureName={FEATURE_GATES.ENTITY_DETAILS}>
              <EntityDefinitions />
            </FeatureGate>
          }
        />
      </Route>
      {/* Data Manager - Specific dataset */}
      <Route path={SPECIFIC_SET_ROUTES.BASE} element={<SpecificSet />}>
        <Route
          path={SPECIFIC_SET_ROUTES.FIELD_SETTINGS}
          element={<DatasetDefinitionScreens.DatasetShow />}
        />
        <Route
          path={SPECIFIC_SET_ROUTES.BASE}
          element={<Metrics mode={'Metric'} />}
        />
        <Route
          path={SPECIFIC_SET_ROUTES.METRICS}
          element={<Metrics mode={'Metric'} />}
        />
        <Route
          path={SPECIFIC_SET_ROUTES.COMPOUND_METRICS}
          element={<Metrics mode="CompoundMetric" />}
        />
        <Route path={SPECIFIC_SET_ROUTES.SHOW_SEARCH} element={<Search />} />
        <Route
          path={SPECIFIC_SET_ROUTES.SHOW_FILTERS}
          element={<DatasetFilters />}
        />
        <Route
          path={SPECIFIC_SET_ROUTES.SHOW_REPORT}
          element={<ValidationReport />}
        />
        <Route
          path={SPECIFIC_SET_ROUTES.TARGET_MANAGER}
          element={
            <PermissionGates.Has
              requiredPermission={PERMISSIONS.DATA_MANAGEMENT.MODIFY_TARGETS}
            >
              <DataManagerTargetManagerScreen />
            </PermissionGates.Has>
          }
        />
        <Route path={SPECIFIC_SET_ROUTES.SHOW_EXPLORE} element={<Explore />} />
      </Route>
      {/* Data Manager - Costs */}
      <Route path={COSTS_SET_ROUTES.ACTIVITY_BASE} element={<CostsSet />}>
        <Route
          path={COSTS_SET_ROUTES.ACTIVITY_BASE_NEW}
          element={<ActivitySettingsCreation />}
        />
        <Route
          path={COSTS_SET_ROUTES.ACTIVITY_BASE_EDIT}
          element={<ActivitySettingsCreation />}
        />
        <Route path={''} element={<CostsShow />} />
      </Route>
      {/* Wallboards */}
      <Route
        path={appRoutes.loggedIn.wallBoardConfigs}
        element={<WallBoardBuilder />}
      />
      <Route
        path={appRoutes.loggedIn.showWallBoardConfig}
        element={<ShowWallBoardBuilder />}
      />
      <Route path={appRoutes.loggedIn.showWallBoard} element={<WallBoard />} />
      <Route path={appRoutes.loggedIn.signOut} element={<SignOutScreen />} />
      <Route
        path={appRoutes.loggedIn.myAccount}
        element={<MyAccountScreen />}
      />
      <Route path={'*'} element={<NotFoundPage />} />
    </Routes>
  );
};

export default Content;
